import { apiErrorMessage, customFetch } from '@agos/api-client'
import {
  BLOCKED_CONTACTS_KEY,
  blockKindLabel,
  ipBlockCaveat,
  orderBlockPath,
  T,
  tt,
  type BlockKind,
} from '@agos/shared'
import { ConfirmDialog, toast } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Ban } from 'lucide-react'
import { useState } from 'react'

import { blockableKinds, defaultTicks, orderBlockBody, orderContactsOf } from './order-block'

/**
 * *Zablokovať* in the archive row's detail - `FRONTEND.md` §9.787, owner O140. The order's phone, e-mail and address,
 * each a tick with its value, and a reason. Rendered for the holder of `marketing_customers_deactivate` only, by its
 * caller.
 */
export function OrderBlockAction({ order }: { order: { id?: number; orderNumber?: string } }) {
  const queryClient = useQueryClient()
  const contacts = orderContactsOf(order)
  const kinds = blockableKinds(contacts)
  const [open, setOpen] = useState(false)
  const [ticks, setTicks] = useState<Record<BlockKind, boolean>>(() => defaultTicks(contacts))
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  if (kinds.length === 0 || typeof order.id !== 'number') return null
  const orderId = order.id

  const send = async () => {
    setSaving(true)
    try {
      await customFetch<unknown>(orderBlockPath(orderId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderBlockBody(ticks, reason)),
      })
      toast({ title: `${tt('Zablokované z objednávky')} #${order.orderNumber ?? String(orderId)}` })
      void queryClient.invalidateQueries({ queryKey: BLOCKED_CONTACTS_KEY })
      setOpen(false)
      setReason('')
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: tt('Zablokovať sa nepodarilo'),
        description: apiErrorMessage(caught, tt('Skúste to znova.')),
      })
    }
    setSaving(false)
  }

  return (
    <>
      <button
        type="button"
        data-order-block={orderId}
        onClick={() => setOpen(true)}
        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold border border-red-500/40 text-red-500 hover:bg-red-500/10"
      >
        <Ban className="w-3.5 h-3.5" /> <T>Zablokovať</T>
      </button>
      <ConfirmDialog
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={() => void send()}
        title={`${tt('Zablokovať z objednávky')} #${order.orderNumber ?? String(orderId)}`}
        description={tt('Objednávky z webu z vybraného neprijmeme.')}
        confirmLabel={tt('Zablokovať')}
        cancelLabel={tt('Zrušiť')}
        variant="danger"
        loading={saving}
        confirmDisabled={!ticks.phone && !ticks.email && !ticks.ip}
      >
        <div className="space-y-2 text-sm" data-order-block-dialog>
          {kinds.map((kind) => (
            <label key={kind} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={ticks[kind]}
                data-order-block-kind={kind}
                onChange={(event) => setTicks((current) => ({ ...current, [kind]: event.target.checked }))}
              />
              <span>
                <span className="text-muted-foreground">{blockKindLabel(kind)}</span>{' '}
                {kind === 'phone' ? contacts.phone : kind === 'email' ? contacts.email : contacts.ip}
              </span>
            </label>
          ))}
          {ticks.ip && <p className="text-xs text-amber-500">{ipBlockCaveat()}</p>}
          <label className="block">
            <span className="block text-xs font-medium mb-1">
              <T>Dôvod (nepovinný)</T>
            </span>
            <input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              data-order-block-reason
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
            />
          </label>
        </div>
      </ConfirmDialog>
    </>
  )
}
